import {
  ArrowCounterClockwise,
  ArrowsClockwiseIcon,
  Broom,
  CaretDownIcon,
  CaretRightIcon,
  CheckCircle,
  Clock,
  PaperPlaneRight,
  Pause,
  Play,
  Queue,
  SpinnerGap,
  Trash,
  XCircle
} from "@phosphor-icons/react";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useState
} from "react";
import type { QueueMessage } from "../../api/queues/model.ts";
import type { QueueAction as QueueCommand, QueueMessageAction } from "@workerstation/shared/resources/object-queue";
import { useQueueExplorer } from "./react.tsx";
import {
  formatResourceBytes,
  ResourceTreeAction,
  ResourceTreeRow
} from "../../ResourceExplorerTree";

interface QueueExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

function MessageIcon({ message }: { message: QueueMessage }) {
  if (message.status === "acked") {
    return <CheckCircle size={13} weight="fill" className="text-status-success" />;
  }
  if (
    message.status === "failed" ||
    message.status === "dead-lettered"
  ) {
    return <XCircle size={13} weight="fill" className="text-status-error" />;
  }
  if (message.status === "delivering") {
    return <SpinnerGap size={13} className="animate-spin" />;
  }
  return <Clock size={13} />;
}

function bodyPreview(body: unknown): string {
  try {
    const serialized = typeof body === "string" ? body : JSON.stringify(body);
    const value = serialized === undefined ? String(body) : serialized;
    return value.length > 160 ? `${value.slice(0, 157)}…` : value;
  } catch {
    return String(body);
  }
}

export function QueueExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: QueueExplorerProps) {
  const { snapshot, dispatch } = useQueueExplorer(workspaceId, collapsed, refreshToken);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["connection"]));
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const { loading, mutating, error } = snapshot;
  useEffect(() => { setExpanded(new Set(["connection"])); setSelectedQueue(null); }, [workspaceId, refreshToken]);
  function toggle(key: string) { setExpanded(current => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; }); }
  const target = (queueName: string) => workspaceId === null ? null : { workspaceId, queueName };
  function send(queueName: string) { const selected = target(queueName); if (selected) dispatch({ _tag: "Send", target: selected }); }
  function queueAction(queueName: string, action: typeof QueueCommand.Type) { const selected = target(queueName); if (selected) dispatch({ _tag: "QueueAction", target: selected, action }); }
  function messageAction(queueName: string, messageId: string, action: typeof QueueMessageAction.Type) { const selected = target(queueName); if (selected) dispatch({ _tag: "MessageAction", target: selected, messageId, action }); }
  const refresh = () => dispatch({ _tag: "Refresh" });

  const selected = snapshot.queues.find(
    (queue) => queue.name === selectedQueue
  );

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            Queues
          </button>
        )}
        <ResourceTreeAction
          label="Send message"
          disabled={!selected || mutating}
          onClick={() => {
            if (selected) send(selected.name);
          }}
        >
          <PaperPlaneRight size={13} weight="fill" />
        </ResourceTreeAction>
        <ResourceTreeAction
          label="Refresh queues"
          onClick={() => refresh()}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId ? (
            snapshot.queues.length ? (
              <>
                <ResourceTreeRow
                  depth={0}
                  label={connectionName || "Workerspace"}
                  detail={`${snapshot.queues.length} queue${snapshot.queues.length === 1 ? "" : "s"}`}
                  icon={<Queue size={14} />}
                  expandable
                  expanded={expanded.has("connection")}
                  onClick={() => toggle("connection")}
                />
                {expanded.has("connection")
                  ? snapshot.queues.map((queue) => {
                      const queueKey = `queue:${queue.name}`;
                      return (
                        <Fragment key={queue.name}>
                          <ResourceTreeRow
                            depth={1}
                            label={queue.name}
                            detail={
                              queue.paused
                                ? "paused"
                                : `${queue.metrics.backlogCount} pending · ${queue.messages.length} stored`
                            }
                            icon={<Queue size={14} weight="fill" />}
                            expandable
                            expanded={expanded.has(queueKey)}
                            onClick={() => {
                              setSelectedQueue(queue.name);
                              toggle(queueKey);
                            }}
                            title={
                              queue.consumer
                                ? `Consumer: ${queue.consumer.workerName}`
                                : `Producers: ${queue.producerBindings.join(", ") || "none"}`
                            }
                            actions={
                              <>
                                <ResourceTreeAction
                                  label={`Send to ${queue.name}`}
                                  disabled={mutating}
                                  onClick={() => send(queue.name)}
                                >
                                  <PaperPlaneRight size={12} weight="fill" />
                                </ResourceTreeAction>
                                <ResourceTreeAction
                                  label={queue.paused ? "Resume queue" : "Pause queue"}
                                  disabled={mutating}
                                  onClick={() =>
                                    queueAction(
                                      queue.name,
                                      queue.paused ? "resume" : "pause"
                                    )
                                  }
                                >
                                  {queue.paused ? (
                                    <Play size={12} weight="fill" />
                                  ) : (
                                    <Pause size={12} weight="fill" />
                                  )}
                                </ResourceTreeAction>
                                <ResourceTreeAction
                                  label={`Purge ${queue.name}`}
                                  disabled={mutating}
                                  onClick={() =>
                                    queueAction(queue.name, "purge")
                                  }
                                >
                                  <Broom size={12} />
                                </ResourceTreeAction>
                              </>
                            }
                          />
                          {expanded.has(queueKey)
                            ? queue.messages.map((message) => (
                                <ResourceTreeRow
                                  key={message.id}
                                  depth={2}
                                  label={bodyPreview(message.body) || message.id}
                                  detail={`${message.status} · ${formatResourceBytes(message.size)} · ${message.attempts} attempt${message.attempts === 1 ? "" : "s"}`}
                                  title={
                                    message.lastError ||
                                    `${message.id}\n${new Date(message.timestamp).toLocaleString()}`
                                  }
                                  icon={<MessageIcon message={message} />}
                                  actions={
                                    <>
                                      <ResourceTreeAction
                                        label="Retry message"
                                        disabled={mutating}
                                        onClick={() =>
                                          messageAction(
                                            queue.name,
                                            message.id,
                                            "retry"
                                          )
                                        }
                                      >
                                        <ArrowCounterClockwise size={12} />
                                      </ResourceTreeAction>
                                      <ResourceTreeAction
                                        label="Delete message"
                                        disabled={mutating}
                                        onClick={() =>
                                          messageAction(
                                            queue.name,
                                            message.id,
                                            "delete"
                                          )
                                        }
                                      >
                                        <Trash size={12} />
                                      </ResourceTreeAction>
                                    </>
                                  }
                                />
                              ))
                            : null}
                        </Fragment>
                      );
                    })
                  : null}
              </>
            ) : (
              <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                Declare a Queue producer binding or consumer trigger in
                cloudflare.config.ts, save the workerspace, then refresh.
              </div>
            )
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to browse Queues.
            </p>
          )}
          {error ? (
            <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
